import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showError, showSuccess } from "toast/toast";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import {
  addressLine1Validation,
  cityValidation,
  emailValidation,
  firstNameValidation,
  lastNameValidation,
  passwordValidation,
  phoneValidation,
  stateValidation,
  statusValidation,
  zipcodeValidation,
} from "validationSchemas/common";
import AgentForm, { AgentFormValues, SelectOption } from "components/AgentsAddEditComponent";
import { uploadCloudFile, getCloudFileById, resolveImageId } from "components/Imageshelper";
import ExistingActiveIdentityConfirmModal, {
  existingActiveIdentityMessage,
  isExistingActiveIdentityConfirmation,
  withExistingActiveIdentityConfirmation,
  type ExistingActiveIdentityError,
} from "components/ExistingActiveIdentityConfirmModal";
import { getAuthCookie } from "../../utils/authCookies";
import { resolveSignageSessionOrganizationId } from "../../api/salesOrderApi";

// Select2 type for brokerage firms
type BrokerageOption = {
  value: string;
  label: string;
  id: string;
  firmDetails: { companyName: string };
};

type Office = {
  _id?: string;
  id?: string;
  officeName: string;
  officeType: string;
  status?: boolean;
  officeAddress: Array<{
    _id: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }>;
};

const parseOfficesResponse = (payload: unknown): Office[] => {
  if (Array.isArray(payload)) return payload as Office[];
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (Array.isArray(record.data)) return record.data as Office[];
    if (Array.isArray(record.results)) return record.results as Office[];
  }
  return [];
};

const getOfficeId = (office: Office) => String(office._id || office.id || "").trim();

type AgentAPI = {
  id: string;
  membership_id?: string;
  _id?: string;
  email: string;
  firstName: string;
  lastName: string;
  phone1: string;
  phone2?: string;
  status: string;
  jobTitle?: string;
  profilePic?: string | null;
  brokerageFirmID?: string;
  created_by?: { id?: string; name?: string };
  address?: Partial<{
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    cityCode: string;
    stateCode: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    phone: string;
  }>;
  professional_details?: Partial<{
    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;
  }>;
  firmDetails?: {
    address?: Partial<{
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      country: string;
      zip: string;
      cityCode: string;
      stateCode: string;
      countryCode: string;
      latitude: number;
      longitude: number;
    }>;
  };
  agentDetails?: Partial<{
    brokerageFirmAffiliation: string;
    brokerageFirmID: string;
    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;
  }>;
  officeIDs?: string[];
  offices?: Array<{
    _id: string;
    id?: string;
    officeName: string;
    officeType: string;
  }>;
  organization?: {
    _id?: string;
    id?: string;
    name?: string;
    type?: string;
    license_type?: string;
  };
  organization_id?: string;
  userType?: string;
  local_user_type?: string;
  licenseType?: string;
  local_license_type?: string;
  addresses?: {
    personal?: {
      address_details?: Partial<{
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        country: string;
        zip: string;
        cityCode: string;
        stateCode: string;
        countryCode: string;
        latitude: number;
        longitude: number;
        phone: string;
      }>;
    };
  };
};

const normalizeAgentStatus = (status?: string) => {
  const upper = (status || "").toUpperCase();
  if (upper === "ACTIVE") return "Active";
  if (upper === "INACTIVE") return "Inactive";
  return status || "Active";
};


const isBrokerageOrganization = (org?: AgentAPI["organization"]) => {
  if (!org) return false;
  const orgType = (org.type || "").toLowerCase();
  const orgLicense = (org.license_type || "").toUpperCase();
  return orgType === "brokerage" || orgLicense === "BROKERAGE_FIRM";
};

const getAgentBrokerageFirmId = (agent: AgentAPI | null) => {
  const org = agent?.organization;
  if (isBrokerageOrganization(org)) {
    return String(org?._id || org?.id || agent?.organization_id || "").trim();
  }
  return String(
    agent?.brokerageFirmID ||
      agent?.agentDetails?.brokerageFirmID ||
      ""
  ).trim();
};

const getAgentBrokerageFirmName = (agent: AgentAPI | null) => {
  const org = agent?.organization;
  if (isBrokerageOrganization(org)) {
    return org?.name || agent?.agentDetails?.brokerageFirmAffiliation || "";
  }
  return agent?.agentDetails?.brokerageFirmAffiliation || "";
};

const getAgentPersonalAddress = (agent: AgentAPI | null) =>
  agent?.addresses?.personal?.address_details ||
  agent?.address ||
  agent?.firmDetails?.address;

const getAgentOfficeId = (agent: AgentAPI | null) => {
  const officeIDs = agent?.officeIDs;
  if (Array.isArray(officeIDs) && officeIDs.length > 0) {
    return String(officeIDs[0] || "").trim();
  }
  return String(agent?.offices?.[0]?._id || agent?.offices?.[0]?.id || "").trim();
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];
const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

const toUrlString = (res: unknown): string | undefined => {
  if (!res) return undefined;
  if (typeof res === "string") return res;
  const r = res as any;
  const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
  return typeof u === "string" ? u : undefined;
};


export default function SignageAddEditBrokerageAgent() {
  const { firmId } = useParams<{ firmId: string }>();
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = Boolean(agentId);
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAuthCookie("sessionToken") || "";
  const organizationId = getAuthCookie("organizationId");
  const sessionLicenseType = String(getAuthCookie("licenseType") || "").trim().toUpperCase();
  const allowExistingMemberAccessConfirm =
    sessionLicenseType === "SIGNAGE_FIRM" || sessionLicenseType === "SIGNAGE_TEAM_MEMBER";
  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // data sources (always arrays)
  const [offices, setOffices] = useState<Office[]>([]);
  const [agent, setAgent] = useState<AgentAPI | null>(null);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [serverImageSrc, setServerImageSrc] = useState<string | undefined>(undefined);
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  const [identityConfirmOpen, setIdentityConfirmOpen] = useState(false);
  const [identityConfirmMessage, setIdentityConfirmMessage] = useState("");
  const [identityConfirming, setIdentityConfirming] = useState(false);
  const [pendingSave, setPendingSave] = useState<{
    payload: Record<string, unknown>;
    url: string;
    method: string;
    errorData: ExistingActiveIdentityError;
  } | null>(null);
  const [memberAccessPrompt, setMemberAccessPrompt] = useState<{
    message: string;
    name: string;
    email: string;
  } | null>(null);
  const [memberAccessConfirmed, setMemberAccessConfirmed] = useState(false);
  const confirmMemberAccessRef = useRef(false);
  const effectiveSignageOrgId = resolveSignageSessionOrganizationId() || organizationId || "";
  
  // State for AsyncPaginate selected options
  const [selectedBrokerageOption, setSelectedBrokerageOption] = useState<BrokerageOption | null>(null);

  // Helper function and constants for AsyncPaginate
  // users-created-only brokerage rows expose organization_id (often no value/id/_id)
  const getBrokerageOrganizationId = (row: any) =>
    String(
      row?.organization_id ??
        row?.organizationId ??
        row?.value ??
        row?.id ??
        row?._id ??
        ""
    ).trim();
  const PAGE_SIZE = 20;
  
  // ===== Brokerage Firm Loader =====
  const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const ownerId = effectiveSignageOrgId;

      if (!ownerId) {
        return { options: [], hasMore: false, additional: { page } };
      }

      try {
        const q = (inputValue || "").trim();

        const params = new URLSearchParams({
          licenseType: "BROKERAGE_FIRM",
          query: q,
          page: String(page),
          limit: String(PAGE_SIZE),
        });

        const url = `${process.env.REACT_APP_API_URL}/customer-groups/organizations/${organizationId}/users-created-only?${params}`;

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch brokerage firms:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();
        const list = Array.isArray(json?.data) ? json.data : [];

        const options: BrokerageOption[] = list
          .map((u: any) => {
            const orgId = getBrokerageOrganizationId(u);
            if (!orgId) return null;
            const companyName =
              u?.firmDetails?.companyName ||
              u?.company_name ||
              u?.label ||
              u?.text ||
              "(no name)";
            return {
              value: orgId,
              label: companyName,
              id: orgId,
              firmDetails: { companyName },
            } as BrokerageOption;
          })
          .filter(Boolean) as BrokerageOption[];

        const currentPage = Number(json?.page ?? page);
        const totalPages = Number(json?.totalPages ?? currentPage);
        const hasMore = currentPage < totalPages && list.length >= PAGE_SIZE;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        console.error("Failed to load brokerage options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [effectiveSignageOrgId, organizationId, token]
  );
  
 const firmIdFromRoute = (firmId || "").trim();
  const normalizePath = (p: string) => p.replace(/\\/g, "/").replace(/^\/+/, "");
  const buildAbsoluteFromRelative = (p: string) => {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    return `${base}/${normalizePath(p)}`;
  };
  useEffect(() => {
    let active = true;

    (async () => {
      const val = agent?.profilePic;
      if (!val) {
        if (active) setServerImageSrc(undefined);
        return;
      }

      if (/^https?:\/\//i.test(val)) {
        if (active) setServerImageSrc(val);
        return;
      }

      if (/^[0-9a-f]{24}$/i.test(val)) {
        setCloudFileId(val);
        try {
          const res = await getCloudFileById(val, token as string);
          console.log(res, "tetetetete")
          const u = toUrlString(res);
          if (active) setServerImageSrc(u);
        } catch {
          if (active) setServerImageSrc(undefined);
        }
        return;
      }

      if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
    })();

    return () => {
      active = false;
    };
  }, [agent?.profilePic, token]); // ⬅️ removed serverImageSrc from deps

  // ---------- fetch firm by firmId from URL ----------
  useEffect(() => {
    if (!firmIdFromRoute || isEdit) return;
    
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/organizations/${firmIdFromRoute}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const firmData = await res.json();
        if (!res.ok || firmData?.error) {
          throw new Error(firmData?.error || firmData?.message || "Failed to load firm");
        }

        const owner = firmData?.owner || {};
        const profile = firmData?.profile || {};
        const firmDetails = firmData?.firmDetails || {};
        const companyName =
          profile?.companyName?.trim() ||
          firmData?.name?.trim() ||
          firmDetails?.companyName?.trim() ||
          `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim() ||
          "(no name)";
        
        const orgId = getBrokerageOrganizationId(firmData) || firmIdFromRoute;
        const brokerageOption: BrokerageOption = {
          value: orgId,
          label: companyName,
          id: orgId,
          firmDetails: { companyName },
        };
        
        setSelectedBrokerageOption(brokerageOption);
        formik.setFieldValue("brokerageFirmAffiliation", orgId, false);
        formik.setFieldValue("officeId", "", false);
        
      } catch (e: any) {
        showError(e.message || "Failed to load firm");
      }
    })();
  }, [firmIdFromRoute, isEdit, token]);

  // ---------- fetch agent (edit) ----------
  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/organization-memberships/${agentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (await res.json()) as AgentAPI;
        if (!res.ok) {
          const errorMessage = (data as any)?.error || (data as any)?.message || "Failed to load agent";
          throw new Error(errorMessage);
        }
        setAgent(data);
      } catch (e: any) {
        showError(e.message || "Failed to load agent");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, agentId, token]);
  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;
  const toCoordinate = (value: unknown) => {
    const coordinate = Number(value ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };

  const saveAgent = async (
    payload: Record<string, unknown>,
    url: string,
    method: string
  ) => {
    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = (await res.json().catch(() => ({}))) as ExistingActiveIdentityError & {
      message?: string;
    };
    return { res, data };
  };

  // ---------- initial values ----------
  const initialValues: AgentFormValues = useMemo(
    () => {
      const addr = getAgentPersonalAddress(agent);
      const firmId = getAgentBrokerageFirmId(agent);
      return {
      // personal
      firstName: agent?.firstName || "",
      lastName: agent?.lastName || "",
      phone1: agent?.phone1 || DEFAULT_TEN_ZEROS,
      phone2: agent?.phone2 || "",
      // account
      email: agent?.email || "",
      password: "",
      // professional
      brokerageFirmAffiliation: isEdit ? firmId : firmId || firmIdFromRoute,
      officeId: getAgentOfficeId(agent),
      agentID: agent?.professional_details?.agentID || agent?.agentDetails?.agentID || "",
      licenseNumber: agent?.professional_details?.licenseNumber || agent?.agentDetails?.licenseNumber || "",
      licenseExpiryDate: agent?.professional_details?.licenseExpiryDate || agent?.agentDetails?.licenseExpiryDate || "",
      statuss: normalizeAgentStatus(agent?.status),
      // address
      addressLine1: addr?.addressLine1 || "",
      addressLine2: addr?.addressLine2 || "",
      city: addr?.city || "",
      state: addr?.state || "",
      country: addr?.country || US_COUNTRY_DEFAULTS.country,
      zipcode: addr?.zip || "",
      cityCode: addr?.cityCode || "",
      stateCode: addr?.stateCode || "",
      countryCode: addr?.countryCode || US_COUNTRY_DEFAULTS.countryCode,
      latitude: toCoordinate(addr?.latitude),
      longitude: toCoordinate(addr?.longitude),
      // profile
      image: null,
      profilePic: agent?.profilePic || null,
      displaySrc: agent?.profilePic || null,
    };
    },
    [agent, firmIdFromRoute]
  );

  // ---------- formik (parent only) ----------
  const formik = useFormik<AgentFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone1: phoneValidation,
      email: emailValidation,
      // password: isEdit ? Yup.string() : passwordValidation,
      // addressLine1: addressLine1Validation,
      // city: cityValidation,
      // state: stateValidation,
      // zipcode: zipcodeValidation,
      // statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      try {
        setFormSubmitting(true);
        let imageFileId = cloudFileId || "";
        if (values.image instanceof File) {
          const uploaded = await uploadCloudFile({ file: values.image, token });
          imageFileId = uploaded.id;
        }
        const generated_id = await resolveImageId(values.image, token, imageFileId);

        const brokerageFirmID = String(
          selectedBrokerageOption?.value ||
            values.brokerageFirmAffiliation ||
            ""
        ).trim();

        const isBrokerageAgent = Boolean(brokerageFirmID);
        if (!effectiveSignageOrgId) {
          throw new Error("Organization ID is missing");
        }

        const agentMembershipId = String(
          agent?.membership_id || agent?._id || agent?.id || agentId || ""
        ).trim();

        if (isEdit && !agentMembershipId) {
          throw new Error("Agent membership ID is missing");
        }

        const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
        const addressCountryCode = values.countryCode?.trim() || US_COUNTRY_DEFAULTS.countryCode;
        const addressLatitude = toCoordinate(values.latitude);
        const addressLongitude = toCoordinate(values.longitude);

        const addressLine1 = String(values.addressLine1 || "").trim();
        const city = String(values.city || "").trim();
        const state = String(values.state || "").trim();
        const zip = String(values.zipcode || "").trim();
        const shouldIncludeAddress = Boolean(
          addressLine1 && city && state && zip
        );

        const payload: Record<string, unknown> = {
          email: values.email,
          password: isEdit ? values.password || "" : values.password || "",
          firstName: values.firstName,
          lastName: values.lastName,
          phone1: withDefault(values.phone1) || values.phone1,
          phone2: values.phone2 || "",
          jobTitle: agent?.jobTitle || "Sales Agent",
          ...(generated_id ? { profilePic: generated_id } : {}),
          status: (values.statuss || "Active").toUpperCase(),
          userType: isBrokerageAgent ? "brokerage_agent" : "agent",
          licenseType: isBrokerageAgent ? "BROKERAGE_AGENT" : "AGENT",
          professional_details: {
            agentID: values.agentID,
            licenseNumber: values.licenseNumber,
            licenseExpiryDate: values.licenseExpiryDate,
          },
          ...(shouldIncludeAddress
            ? {
                address: {
                  addressLine1,
                  addressLine2: values.addressLine2 || "",
                  city,
                  state,
                  country: addressCountry,
                  zip,
                  phone: withDefault(values.phone1) || values.phone1,
                  latitude: addressLatitude,
                  longitude: addressLongitude,
                  cityCode: values.cityCode,
                  stateCode: values.stateCode,
                  countryCode: addressCountryCode,
                },
              }
            : {}),
        };

        if (isBrokerageAgent) {
          payload.brokerageFirmID = brokerageFirmID;
          if (values.officeId) {
            payload.officeIDs = [values.officeId];
          }
        }
        if (allowExistingMemberAccessConfirm && confirmMemberAccessRef.current) {
          payload.confirm_existing_brokerage_member_access = true;
        }
        confirmMemberAccessRef.current = false;

        const targetOrganizationID = isBrokerageAgent
          ? brokerageFirmID
          : effectiveSignageOrgId;
        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/organizations/${targetOrganizationID}/memberships/agents/${agentMembershipId}`
          : `${process.env.REACT_APP_API_URL}/organizations/${targetOrganizationID}/memberships/agents`;
        const method = isEdit ? "PATCH" : "POST";

        const { res, data } = await saveAgent(payload, url, method);

        if (
          allowExistingMemberAccessConfirm &&
          data?.code === "BROKERAGE_MEMBER_ACCESS_CONFIRMATION_REQUIRED"
        ) {
          const existing = (data as ExistingActiveIdentityError & {
            existing_member?: { name?: string; email?: string };
          }).existing_member;
          setMemberAccessConfirmed(false);
          setMemberAccessPrompt({
            message:
              data.message ||
              "An agent with this email already exists in the brokerage. Do you want to ask the brokerage to give your vendor access to this agent?",
            name: String(existing?.name || "").trim(),
            email: String(existing?.email || "").trim(),
          });
          return;
        }

        if (isExistingActiveIdentityConfirmation(res.status, data)) {
          setPendingSave({ payload, url, method, errorData: data });
          setIdentityConfirmMessage(existingActiveIdentityMessage(data));
          setIdentityConfirmOpen(true);
          return;
        }

        if (!res.ok || data?.error) {
          throw new Error(data?.error || data?.message || "Failed to save agent");
        }

        showSuccess(isEdit ? "Agent updated successfully!" : "Agent added successfully!");
        if (isEdit) {
          // navigate(`${baseRoute}/agents`);
          navigate(-1);
        } else {
          navigate(-1);
        }
      } catch (e: any) {
        showError(e.message || "Failed to save agent");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  const closeMemberAccessPrompt = () => {
    if (formSubmitting) return;
    confirmMemberAccessRef.current = false;
    setMemberAccessConfirmed(false);
    setMemberAccessPrompt(null);
  };

  const confirmExistingMemberAccess = () => {
    if (!memberAccessConfirmed) {
      showError("Select the confirmation to continue.");
      return;
    }
    confirmMemberAccessRef.current = true;
    setMemberAccessPrompt(null);
    formik.handleSubmit();
  };

  const handleIdentityConfirmClose = () => {
    setIdentityConfirmOpen(false);
    setPendingSave(null);
    navigate(-1);
  };

  const handleIdentityConfirmOk = async () => {
    if (!pendingSave) return;
    setIdentityConfirming(true);
    try {
      const confirmedPayload = withExistingActiveIdentityConfirmation(
        pendingSave.payload,
        pendingSave.errorData
      );
      const { res, data } = await saveAgent(
        confirmedPayload,
        pendingSave.url,
        pendingSave.method
      );
      if (!res.ok || data?.error) {
        throw new Error(data?.error || data?.message || "Failed to save agent");
      }
      setIdentityConfirmOpen(false);
      setPendingSave(null);
      showSuccess(isEdit ? "Agent updated successfully!" : "Agent added successfully!");
      if (isEdit) {
        navigate(`${baseRoute}/agents`);
      } else {
        navigate(-1);
      }
    } catch (e: any) {
      showError(e.message || "Failed to save agent");
    } finally {
      setIdentityConfirming(false);
    }
  };

  // This useEffect is now handled by the fetch firm by firmId useEffect above

  const handleBrokerageChange = useCallback(
    (option: BrokerageOption | null) => {
      setSelectedBrokerageOption(option);
      const orgId = String(option?.value || "").trim();
      formik.setFieldValue("brokerageFirmAffiliation", orgId, false);
      formik.setFieldValue("officeId", "", false);
    },
    [formik]
  );

  // fetch offices when firm changes (route firmId, formik value, or selected option)
  useEffect(() => {
    const brokerageOrgId = String(
      selectedBrokerageOption?.value ||
        formik.values.brokerageFirmAffiliation ||
        ""
    ).trim();
    if (!brokerageOrgId) {
      setOffices([]);
      return;
    }

    let active = true;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/organizations/${brokerageOrgId}/offices-search`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (!res.ok || data?.error) {
          throw new Error(data?.error || data?.message || "Failed to fetch offices");
        }
        if (active) setOffices(parseOfficesResponse(data));
      } catch (e: any) {
        if (active) {
          showError(e.message || "Failed to fetch offices");
          setOffices([]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [
    selectedBrokerageOption?.value,
    formik.values.brokerageFirmAffiliation,
    token,
  ]);

  // ---------- HYDRATE firm/office on EDIT ----------
  // 1) map agent firm name or created_by.id to firm ID → set in Formik and selectedBrokerageOption
  useEffect(() => {
    if (!isEdit || !agent) return;

    const resolvedFirmId = getAgentBrokerageFirmId(agent);
    const firmName = getAgentBrokerageFirmName(agent);

    if (resolvedFirmId) {
      const brokerageOption: BrokerageOption = {
        value: resolvedFirmId,
        label: firmName || "(no name)",
        id: resolvedFirmId,
        firmDetails: { companyName: firmName || "(no name)" },
      };
      setSelectedBrokerageOption(brokerageOption);
      if (formik.values.brokerageFirmAffiliation !== resolvedFirmId) {
        formik.setFieldValue("brokerageFirmAffiliation", resolvedFirmId, false);
      }
      return;
    }

    setSelectedBrokerageOption(null);
    formik.setFieldValue("brokerageFirmAffiliation", "", false);
    formik.setFieldValue("officeId", "", false);
    setOffices([]);
  }, [isEdit, agent]);

  // 2) after offices arrive, set agent's officeId
  useEffect(() => {
    if (!isEdit || !agent || !offices.length) return;
    const agentOfficeId = getAgentOfficeId(agent);
    if (agentOfficeId && formik.values.officeId !== agentOfficeId) {
      formik.setFieldValue("officeId", agentOfficeId, false);
    }
  }, [isEdit, agent, offices]);

  if (loading) return <div className="content">Loading...</div>;

  // options for child (always arrays) - now using AsyncPaginate
  console.log(selectedBrokerageOption,"selectedBrokerageOption")
  const brokerageFirmOptions: SelectOption[] = selectedBrokerageOption ? [{
    value: selectedBrokerageOption.value,
    label: selectedBrokerageOption.firmDetails.companyName,
  }] : [];

  const officeOptions: SelectOption[] = (offices ?? []).map((o) => ({
    value: getOfficeId(o),
    label: o.officeName || "(no name)",
  })).filter((o) => o.value);
  console.log(officeOptions,"officeOptionsmain")
  const joinUrl = (base?: string, path?: string) => {
    if (!path) return "";
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const b = base.replace(/\/+$/, "");
    const p = path.replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const displaySrc: string | undefined =
    serverImageSrc ??
    (typeof formik.values.profilePic === "string" && /^\/?uploads\//i.test(formik.values.profilePic)
      ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.profilePic)
      : undefined);
console.log(brokerageFirmOptions,"brokerageFirmOptionsmainparent")

  return (
    <>
      <AgentForm
        isEdit={isEdit}
        formik={formik}
        brokerageFirmOptions={brokerageFirmOptions}
        officeOptions={officeOptions}
        statusOptions={STATUS_OPTIONS}
        displaySrc={displaySrc}
        onCancel={() => navigate(-1)}
        onSubmit={() => formik.handleSubmit()}
        submitting={formSubmitting}
        type={"Brokerage_Agent"}
        enableGoogleAddressDetailsFlow
        // AsyncPaginate props
        loadBrokerageOptions={loadBrokerageOptions}
        selectedBrokerageOption={selectedBrokerageOption}
        onBrokerageChange={handleBrokerageChange}
        memberAccessPrompt={memberAccessPrompt}
        memberAccessConfirmed={memberAccessConfirmed}
        onMemberAccessConfirmedChange={setMemberAccessConfirmed}
        onMemberAccessCancel={closeMemberAccessPrompt}
        onMemberAccessSave={confirmExistingMemberAccess}
      />
      <ExistingActiveIdentityConfirmModal
        open={identityConfirmOpen}
        message={identityConfirmMessage}
        confirming={identityConfirming}
        onClose={handleIdentityConfirmClose}
        onConfirm={handleIdentityConfirmOk}
      />
    </>
  );
}
